/*!
 * tp-enhancements.js
 * Coastal Gummies — "Premium Polish" interaction layer (vanilla JS, no deps)
 *
 * What this does:
 *   1. Reveals elements with class="tp-reveal" (fade+rise) as they scroll
 *      into view, by toggling class "tp-reveal--in".
 *   2. Marks <html class="tp-js-ready"> ONLY when it is safe to animate
 *      (JS ran, IntersectionObserver exists, motion is allowed). The CSS
 *      in tp-enhancements.css only hides/animates .tp-reveal elements
 *      when this class is present — see that file for the full
 *      degrade-gracefully contract.
 *   3. Optionally marks lazy images tagged class="tp-shimmer" as
 *      class="tp-loaded" once loaded, to stop the shimmer sweep.
 *
 * Safe by design:
 *   - No-ops entirely if prefers-reduced-motion: reduce.
 *   - No-ops the observer (reveals everything immediately) if
 *     IntersectionObserver is unavailable (old browsers).
 *   - Never throws if .tp-reveal / .tp-shimmer elements don't exist.
 *   - Does not touch any existing theme markup or globals.
 */
(function () {
  "use strict";

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (prefersReducedMotion) {
    // Respect the OS/browser setting completely: no class flip, no
    // animation hooks. CSS keeps .tp-reveal elements at opacity:1 by
    // default, so nothing needs to happen here at all.
    return;
  }

  var supportsIO = "IntersectionObserver" in window;

  function revealAllImmediately() {
    var els = document.querySelectorAll(".tp-reveal");
    for (var i = 0; i < els.length; i++) {
      els[i].classList.add("tp-reveal--in");
    }
  }

  // Tastefully opt content into scroll-reveal WITHOUT editing any section
  // markup: tag section headings + product cards inside the main content area
  // only (Dawn's header/footer live OUTSIDE #MainContent, so they're never
  // touched). Idempotent — safe to call repeatedly.
  function autoTag() {
    var main = document.getElementById("MainContent");
    if (!main) return;
    // Headings only (always in normal document flow → always reveal reliably).
    // Deliberately NOT tagging cards/carousel items, which can sit off-screen and
    // risk never intersecting.
    var nodes = main.querySelectorAll(".shopify-section h2");
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add("tp-reveal", "tp-reveal--heading");
  }

  function initReveal() {
    var targets = document.querySelectorAll(".tp-reveal");
    if (!targets.length) return;

    if (!supportsIO) {
      // Old browser: just show everything, no animation.
      revealAllImmediately();
      return;
    }

    // Only now do we opt into the CSS's hidden starting state, since we
    // know we can reliably reveal elements again via the observer.
    document.documentElement.classList.add("tp-js-ready");

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("tp-reveal--in");
            obs.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
    );

    targets.forEach(function (el) { observer.observe(el); });

    // FAIL-SAFE: content must NEVER stay hidden. Reveal anything already near the
    // viewport on the next frame, and force-reveal EVERYTHING after 2.5s no matter
    // what (guards against a missed observer / off-screen element sticking at
    // opacity:0). Scroll animation still plays for anything scrolled to first.
    requestAnimationFrame(function () {
      var vh = window.innerHeight || 800;
      targets.forEach(function (el) {
        if (el.getBoundingClientRect().top < vh * 1.15) el.classList.add("tp-reveal--in");
      });
    });
    setTimeout(function () {
      document.querySelectorAll(".tp-reveal:not(.tp-reveal--in)").forEach(function (el) {
        el.classList.add("tp-reveal--in");
      });
    }, 2500);
  }

  function initShimmer() {
    var imgs = document.querySelectorAll(".tp-shimmer");
    if (!imgs.length) return;

    imgs.forEach(function (el) {
      var img = el.tagName === "IMG" ? el : el.querySelector("img");
      if (!img) return;

      if (img.complete && img.naturalWidth > 0) {
        el.classList.add("tp-loaded");
        return;
      }

      img.addEventListener(
        "load",
        function () {
          el.classList.add("tp-loaded");
        },
        { once: true }
      );
      img.addEventListener(
        "error",
        function () {
          el.classList.add("tp-loaded");
        },
        { once: true }
      );
    });
  }

  // Smooth open/close for the product page's native <details> accordions
  // (Ingredients / Suggested Use). Native <details> can't transition its open
  // state, so we intercept the toggle and let the CSS grid-rows trick animate.
  // Scoped to .product__accordion so header/menu/search <details> are untouched.
  function initAccordion() {
    var accordions = document.querySelectorAll(".product__accordion > details");
    accordions.forEach(function (details) {
      var summary = details.querySelector(":scope > summary");
      if (!summary) return;
      summary.addEventListener("click", function (e) {
        if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        e.preventDefault();
        if (details.open) {
          requestAnimationFrame(function () { details.removeAttribute("open"); });
        } else {
          details.setAttribute("open", "");
        }
      });
    });
  }

  // Gradual, scroll-linked header transparency (replaces the old binary
  // "transparent once scrolled past" snap, which flipped too fast). Ramps the
  // header background from fully opaque at the very top to frosted-transparent
  // as you scroll down, so the change feels smooth and continuous.
  function initHeaderFade() {
    var header = document.querySelector(".section-header .header-wrapper") ||
                 document.querySelector(".header-wrapper");
    if (!header) return;
    // MIN_ALPHA = background opacity at full scroll (lower = more see-through).
    // MAX_BLUR  = frosted-glass amount (lower = less "glassy"/distracting).
    // Dialed down from 0.2/8 so the header recedes and stops warping content
    // moving behind it — feedback was it read too glassy.
    var RAMP = 700, MIN_ALPHA = 0.08, MAX_BLUR = 2.5, ticking = false;
    function update() {
      ticking = false;
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var t = Math.min(Math.max(y, 0) / RAMP, 1);
      var alpha = (1 - t * (1 - MIN_ALPHA)).toFixed(3);
      var blur = (t * MAX_BLUR).toFixed(1);
      header.style.backgroundColor = "rgba(var(--color-background), " + alpha + ")";
      header.style.webkitBackdropFilter = "blur(" + blur + "px)";
      header.style.backdropFilter = "blur(" + blur + "px)";
    }
    window.addEventListener("scroll", function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  function init() {
    autoTag();
    initReveal();
    initShimmer();
    initAccordion();
    initHeaderFade();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-scan when the Shopify theme editor injects/reorders sections, so
  // merchants editing the theme still see reveal targets picked up.
  document.addEventListener("shopify:section:load", function (evt) {
    if (!evt.target) return;
    autoTag();
    var newTargets = evt.target.querySelectorAll
      ? evt.target.querySelectorAll(".tp-reveal")
      : [];
    if (!newTargets.length) return;

    if (!supportsIO) {
      for (var i = 0; i < newTargets.length; i++) {
        newTargets[i].classList.add("tp-reveal--in");
      }
      return;
    }

    document.documentElement.classList.add("tp-js-ready");
    newTargets.forEach(function (el) {
      if (el.classList.contains("tp-reveal--in")) return;
      var obs = new IntersectionObserver(
        function (entries, o) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add("tp-reveal--in");
              o.unobserve(entry.target);
            }
          });
        },
        { root: null, rootMargin: "0px 0px -10% 0px", threshold: 0.1 }
      );
      obs.observe(el);
    });
  });
})();
